import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight, ChevronDown, ChevronUp, Flag } from 'lucide-react';

interface RiderStanding {
  pos: number;
  rider: string;
  team: string;
  points: number;
  flag: string;
}

// MotoGP 2026 WCC standings after Catalan GP (Round 7)
const ROUND_LABEL = 'After Catalan GP (R7)';
const STANDINGS: RiderStanding[] = [
  { pos: 1, rider: 'M. Bezzecchi', team: 'Aprilia', points: 156, flag: '🇮🇹' },
  { pos: 2, rider: 'J. Martin', team: 'Aprilia', points: 154, flag: '🇪🇸' },
  { pos: 3, rider: 'P. Acosta', team: 'KTM', points: 130, flag: '🇪🇸' },
  { pos: 4, rider: 'A. Marquez', team: 'Ducati', points: 118, flag: '🇪🇸' },
  { pos: 5, rider: 'M. Marquez', team: 'Ducati', points: 105, flag: '🇪🇸' },
];

const NEXT_RACE = {
  name: 'Dutch TT',
  date: new Date(Date.UTC(2026, 5, 28, 13, 0)), // June 28, 2026 13:00 UTC
  circuit: 'TT Circuit Assen',
};

function daysUntilRace(): number {
  const now = Date.now();
  const diff = NEXT_RACE.date.getTime() - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const TEAM_COLOR: Record<string, string> = {
  Aprilia: 'text-purple-400',
  Ducati: 'text-red-400',
  KTM: 'text-orange-400',
  Yamaha: 'text-blue-400',
  Honda: 'text-slate-400',
};

export function MotoGPStandingsWidget() {
  const [collapsed, setCollapsed] = useState(false);
  const days = daysUntilRace();

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-purple-500/5 to-transparent text-left"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 flex-shrink-0">
          <Trophy className="h-4 w-4 text-purple-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary text-sm">MotoGP 2026 Standings</h3>
          <span className="text-[10px] text-text-tertiary">{ROUND_LABEL}</span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronUp className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        )}
      </button>

      {!collapsed && (
        <>
          {days > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border-primary bg-bg-tertiary/50">
              <Flag className="h-3.5 w-3.5 text-text-tertiary flex-shrink-0" />
              <span className="text-xs text-text-secondary">
                <span className="font-medium">{NEXT_RACE.name}</span>
                {' '}in{' '}
                <span className="font-bold text-purple-400 tabular-nums">{days}d</span>
              </span>
            </div>
          )}

          <div className="divide-y divide-border-primary">
            {STANDINGS.map((d) => (
              <div key={d.pos} className="flex items-center gap-3 px-4 py-2.5">
                <span className={`w-5 text-center text-xs font-bold tabular-nums ${d.pos === 1 ? 'text-yellow-400' : 'text-text-tertiary'}`}>
                  {d.pos}
                </span>
                <span className="text-sm flex-shrink-0">{d.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{d.rider}</p>
                  <p className={`text-[11px] ${TEAM_COLOR[d.team] ?? 'text-text-tertiary'}`}>{d.team}</p>
                </div>
                <span className="text-sm font-bold text-text-primary tabular-nums">{d.points}</span>
                <span className="text-[10px] text-text-tertiary">pts</span>
              </div>
            ))}
          </div>

          <Link
            to="/guides/motogp-2026-season-guide"
            className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            Full season guide
            <ChevronRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </div>
  );
}
